import { useEffect, useState, useMemo } from "react";
import { Pin, PinOff, Plus, X, CheckCircle, Clock, XCircle, ThumbsUp, ThumbsDown, Palette, Bookmark, Info } from "lucide-react";
import { SubmitQuoteCard } from "@/components/submissions/SubmitQuoteCard";
import { ThemePicker } from "@/components/ThemePicker";
import { toast } from "sonner";
import { MetricBar } from "@/components/MetricBar";
import { AuthErrorPrompt } from "@/components/AuthErrorPrompt";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useGetProfileQuoteClusters,
  useGetQuotesFavorites,
  useGetProfilePinnedQuotes,
  useUpdateProfilePinnedQuotes,
  useUpdateNotificationSettings,
  useUpdateContentPreferences,
  getGetProfileQuoteClustersQueryKey,
  getGetQuotesFavoritesQueryKey,
  getGetProfilePinnedQuotesQueryKey,
  getGetProfileMeQueryKey,
  customFetch,
  type PinQuoteBody,
  type QuoteWithTags,
  type UpdateNotificationSettingsBody,
  type UpdateContentPreferencesBody,
  type SubmissionWithVotes,
} from "@workspace/api-client-react";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthEnabled } from "@/lib/authQuery";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Local stubs — `/api/profile/stats` and `/api/profile/referral-code` exist on
// the server but are not in the OpenAPI spec yet, so orval did not generate hooks for them.
type ProfileStats = {
  totalFavorites: number;
  submissionCounts: {
    total: number;
    approved: number;
    pending: number;
    rejected: number;
  };
  totalUpvotesReceived: number;
  activityData: { date: string; count: number }[];
};
const getGetProfileStatsQueryKey = () => ["/api/profile/stats"] as const;
function useGetProfileStats(options?: { query?: { queryKey?: readonly unknown[]; enabled?: boolean } }) {
  return useQuery({
    queryKey: options?.query?.queryKey ?? getGetProfileStatsQueryKey(),
    queryFn: () => customFetch<ProfileStats>("/api/profile/stats", { method: "GET" }),
    enabled: options?.query?.enabled,
  });
}

function isAuthError(error: unknown): boolean {
  return (error as any)?.status === 401 || (error as any)?.status === 403;
}


const HOUR_LABELS: Record<number, string> = {
  0: "12 AM", 1: "1 AM", 2: "2 AM", 3: "3 AM", 4: "4 AM", 5: "5 AM",
  6: "6 AM", 7: "7 AM", 8: "8 AM", 9: "9 AM", 10: "10 AM", 11: "11 AM",
  12: "12 PM", 13: "1 PM", 14: "2 PM", 15: "3 PM", 16: "4 PM", 17: "5 PM",
  18: "6 PM", 19: "7 PM", 20: "8 PM", 21: "9 PM", 22: "10 PM", 23: "11 PM",
};


function NotificationsCard({
  initialEnabled,
  initialFrequency,
  initialHour,
  initialEmail,
}: {
  initialEnabled: boolean;
  initialFrequency: "daily" | "weekly";
  initialHour: number;
  initialEmail: string | null;
}) {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const mutation = useUpdateNotificationSettings();

  const [enabled, setEnabled] = useState(initialEnabled);
  const [frequency, setFrequency] = useState<"daily" | "weekly">(initialFrequency);
  const [hour, setHour] = useState(initialHour);
  const [email, setEmail] = useState(initialEmail ?? currentUser?.email ?? "");

  // Keep local state in sync when profile loads/updates
  useEffect(() => {
    setEnabled(initialEnabled);
    setFrequency(initialFrequency);
    setHour(initialHour);
    setEmail(initialEmail ?? currentUser?.email ?? "");
  }, [initialEnabled, initialFrequency, initialHour, initialEmail, currentUser?.email]);

  const handleSave = () => {
    const body: UpdateNotificationSettingsBody = {
      notifyEnabled: enabled,
      notifyFrequency: frequency,
      notifyHour: hour,
      notifyEmail: email.trim() || undefined,
    };

    mutation.mutate(
      { data: body },
      {
        onSuccess: (updated) => {
          queryClient.setQueryData(getGetProfileMeQueryKey(), updated);
          toast.success("Notification settings saved");
        },
        onError: () => {
          toast.error("Failed to save settings — please try again");
        },
      },
    );
  };

  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm xl:col-span-2">
      <h3 className="mb-1 text-xl font-semibold text-stone-900">Email notifications</h3>
      <p className="mb-6 text-sm text-stone-500">
        Get a quote delivered to your inbox on your schedule.
      </p>

      <div className="space-y-5">
        <div className="flex items-center justify-between rounded-2xl bg-stone-50 px-4 py-3">
          <Label htmlFor="notify-toggle" className="text-sm font-medium text-stone-700 cursor-pointer">
            Enable email notifications
          </Label>
          <Switch
            id="notify-toggle"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>

        <div
          className={`space-y-4 transition-opacity duration-150 ${
            enabled ? "opacity-100" : "opacity-40 pointer-events-none"
          }`}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-sm text-stone-600">Frequency</Label>
              <Select
                value={frequency}
                onValueChange={(v) => setFrequency(v as "daily" | "weekly")}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly (Monday)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm text-stone-600">Send at (UTC)</Label>
              <Select
                value={String(hour)}
                onValueChange={(v) => setHour(Number(v))}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {HOUR_LABELS[i]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notify-email" className="text-sm text-stone-600">
              Send to
            </Label>
            <Input
              id="notify-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl"
            />
          </div>
        </div>

        <div className="pt-1">
          <button
            onClick={handleSave}
            disabled={mutation.isPending}
            className="rounded-xl bg-stone-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-stone-700 disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ContentPreferencesCard({
  initialAllowProfane,
  initialAllowSexual,
  isLoading,
}: {
  initialAllowProfane: boolean;
  initialAllowSexual: boolean;
  isLoading: boolean;
}) {
  const queryClient = useQueryClient();
  const mutation = useUpdateContentPreferences();
  const [allowProfane, setAllowProfane] = useState(initialAllowProfane);
  const [allowSexual, setAllowSexual] = useState(initialAllowSexual);

  useEffect(() => {
    setAllowProfane(initialAllowProfane);
    setAllowSexual(initialAllowSexual);
  }, [initialAllowProfane, initialAllowSexual]);

  const isDirty =
    allowProfane !== initialAllowProfane || allowSexual !== initialAllowSexual;

  const handleSave = () => {
    const body: UpdateContentPreferencesBody = {
      allowProfaneQuotes: allowProfane,
      allowSexualQuotes: allowSexual,
    };

    mutation.mutate(
      { data: body },
      {
        onSuccess: (updated) => {
          queryClient.setQueryData(getGetProfileMeQueryKey(), updated);
          queryClient.invalidateQueries({
            predicate: ({ queryKey }) => {
              const path = queryKey[0];
              return (
                typeof path === "string" &&
                (path.startsWith("/api/quotes") ||
                  path.startsWith("/api/authors") ||
                  path === "/api/profile/pinned-quotes")
              );
            },
          });
          toast.success("Content preferences saved");
        },
        onError: () => {
          toast.error("Failed to save preferences — please try again");
        },
      },
    );
  };

  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm xl:col-span-2">
      <h3 className="mb-1 text-xl font-semibold text-stone-900">Content preferences</h3>
      <p className="mb-6 text-sm text-stone-500">
        Flagged quotes are hidden by default. Choose which categories you are comfortable seeing.
      </p>

      {isLoading ? (
        <div className="space-y-3">
          <div className="h-16 animate-pulse rounded-2xl bg-stone-100" />
          <div className="h-16 animate-pulse rounded-2xl bg-stone-100" />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-6 rounded-2xl bg-stone-50 px-4 py-3">
            <div>
              <Label htmlFor="allow-profane-quotes" className="cursor-pointer text-sm font-medium text-stone-700">
                Show quotes with profanity
              </Label>
              <p className="mt-0.5 text-xs leading-relaxed text-stone-500">
                Includes quotes containing words identified as profane.
              </p>
            </div>
            <Switch
              id="allow-profane-quotes"
              checked={allowProfane}
              onCheckedChange={setAllowProfane}
              aria-label="Show quotes with profanity"
            />
          </div>

          <div className="flex items-center justify-between gap-6 rounded-2xl bg-stone-50 px-4 py-3">
            <div>
              <Label htmlFor="allow-sexual-quotes" className="cursor-pointer text-sm font-medium text-stone-700">
                Show quotes with sexual context
              </Label>
              <p className="mt-0.5 text-xs leading-relaxed text-stone-500">
                Includes quotes flagged for sexual themes or references.
              </p>
            </div>
            <Switch
              id="allow-sexual-quotes"
              checked={allowSexual}
              onCheckedChange={setAllowSexual}
              aria-label="Show quotes with sexual context"
            />
          </div>

          <div className="pt-2">
            <button
              onClick={handleSave}
              disabled={!isDirty || mutation.isPending}
              className="rounded-xl bg-stone-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-stone-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mutation.isPending ? "Saving…" : "Save preferences"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Resolution fields the backend adds to owner submission history; optional until OpenAPI is updated.
type ResolvedSubmission = SubmissionWithVotes & {
  resolution?: "duplicate" | "withdrawn" | "corrected" | string | null;
  canonicalText?: string | null;
  canonicalAuthor?: string | null;
  canonicalWork?: string | null;
};

function SubmissionResolutionNote({ s }: { s: ResolvedSubmission }) {
  if (!s.resolution) return null;
  const changed =
    (s.canonicalText && s.canonicalText !== s.text) || (s.canonicalAuthor && s.canonicalAuthor !== s.author);
  if (s.resolution === "withdrawn") {
    return (
      <div className="mt-2 rounded-xl border border-stone-200 bg-white px-3 py-2">
        <p className="text-xs text-stone-600 leading-relaxed">
          <span className="font-medium">Withdrawn by a curator.</span> It is no longer shown in the library. Your original submission is kept below for your records.
        </p>
      </div>
    );
  }
  if (s.resolution === "duplicate") {
    return (
      <div className="mt-2 rounded-xl border border-sky-100 bg-sky-50 px-3 py-2">
        <p className="text-xs text-sky-800 leading-relaxed">
          <span className="font-medium">Matched an existing quote.</span> Your submission was resolved to a quote already in the library{s.canonicalAuthor ? `, credited to ${s.canonicalAuthor}` : ""}. You remain the submitter of record.
        </p>
      </div>
    );
  }
  if (!changed) return null;
  return (
    <div className="mt-2 rounded-xl border border-amber-100 bg-amber-50 px-3 py-2">
      <p className="text-xs text-amber-900 leading-relaxed">
        <span className="font-medium">Corrected by a curator:</span>{" "}
        {s.canonicalText ? <span className="italic">"{s.canonicalText}"</span> : null}
        {s.canonicalAuthor ? ` — ${s.canonicalAuthor}` : ""}
      </p>
    </div>
  );
}

function SubmissionHistoryList({
  submissions,
}: {
  submissions: SubmissionWithVotes[];
}) {
  if (submissions.length === 0) return null;

  return (
    <div className="mt-6 space-y-3">
      <h4 className="text-sm font-semibold text-stone-600 uppercase tracking-wide">Submission history</h4>
      <div className="space-y-3">
        {submissions.map((s) => (
          <div
            key={s.id}
            className="rounded-2xl border border-stone-100 bg-stone-50 px-4 py-3"
          >
            <div className="flex items-start justify-between gap-3 mb-1">
              <p className="text-sm text-stone-800 italic line-clamp-2 leading-relaxed flex-1">
                "{s.text}"
              </p>
              {(s as ResolvedSubmission).resolution === "withdrawn" ? (
                <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-stone-200 text-stone-600 px-2.5 py-0.5 text-xs font-medium">
                  <XCircle className="h-3 w-3" />
                  Withdrawn
                </span>
              ) : (s as ResolvedSubmission).resolution === "duplicate" ? (
                <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-sky-100 text-sky-700 px-2.5 py-0.5 text-xs font-medium">
                  <CheckCircle className="h-3 w-3" />
                  Already in library
                </span>
              ) : s.status === "approved" ? (
                <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-0.5 text-xs font-medium">
                  <CheckCircle className="h-3 w-3" />
                  {s.isUserAuthor ? "Added to community" : "Added to library"}
                </span>
              ) : s.status === "rejected" ? (
                <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-red-100 text-red-600 px-2.5 py-0.5 text-xs font-medium">
                  <XCircle className="h-3 w-3" />
                  Not approved
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-amber-100 text-amber-700 px-2.5 py-0.5 text-xs font-medium">
                  <Clock className="h-3 w-3" />
                  In review
                </span>
              )}
            </div>
            <p className="text-xs text-stone-500">
              — {s.author}
              {((s as ResolvedSubmission).canonicalWork ?? s.source) ? `, ${(s as ResolvedSubmission).canonicalWork ?? s.source}` : ""}
              <span className="mx-1.5 text-stone-300">·</span>
              {new Date(s.submittedAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </p>
            {s.status === "approved" && !(s as ResolvedSubmission).resolution?.match(/^(withdrawn|duplicate)$/) && (
              <div className="mt-2 flex items-center gap-3">
                {(() => {
                  const net = s.upvotes - s.downvotes;
                  return (
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${net >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                      <ThumbsUp className="h-3 w-3" />
                      Net {net >= 0 ? "+" : ""}{net}
                    </span>
                  );
                })()}
                <span className="inline-flex items-center gap-1 text-xs text-stone-400 font-medium">
                  <ThumbsUp className="h-3 w-3" />
                  {s.upvotes}
                  <ThumbsDown className="h-3 w-3 ml-1" />
                  {s.downvotes}
                </span>
              </div>
            )}
            <SubmissionResolutionNote s={s as ResolvedSubmission} />
            {s.status === "rejected" && s.rejectionReason && (
              <div className="mt-2 rounded-xl bg-red-50 border border-red-100 px-3 py-2">
                <p className="text-xs text-red-700 leading-relaxed">
                  <span className="font-medium">Reason:</span> {s.rejectionReason}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PinPickerModal({
  favorites,
  currentPinnedQuoteIds,
  onPick,
  onClose,
}: {
  favorites: QuoteWithTags[];
  currentPinnedQuoteIds: Set<number>;
  onPick: (quote: QuoteWithTags) => void;
  onClose: () => void;
}) {
  const available = favorites.filter((q) => !currentPinnedQuoteIds.has(q.id));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-stone-100">
          <h3 className="text-lg font-semibold text-stone-900">Choose a quote to pin</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-stone-100 transition-colors"
          >
            <X className="h-5 w-5 text-stone-500" />
          </button>
        </div>
        <div className="overflow-y-auto max-h-[60vh] p-4 space-y-2">
          {available.length === 0 ? (
            <p className="text-center text-sm text-stone-400 py-8">
              All your saved quotes are already pinned
            </p>
          ) : (
            available.map((quote) => (
              <button
                key={quote.id}
                onClick={() => onPick(quote)}
                className="w-full text-left rounded-2xl bg-stone-50 hover:bg-amber-50 border border-transparent hover:border-amber-200 p-4 transition-all"
              >
                <p className="text-sm text-stone-800 line-clamp-3 leading-relaxed">
                  "{quote.text}"
                </p>
                <p className="mt-1 text-xs text-stone-500">— {quote.author}</p>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function PinnedQuoteSlot({
  position,
  pinnedQuote,
  onPin,
  onUnpin,
}: {
  position: number;
  pinnedQuote: QuoteWithTags | null;
  onPin: () => void;
  onUnpin: () => void;
}) {
  if (!pinnedQuote) {
    return (
      <button
        onClick={onPin}
        className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-stone-200 hover:border-amber-300 hover:bg-amber-50 transition-all p-6 text-center min-h-[120px]"
      >
        <Plus className="h-5 w-5 text-stone-400" />
        <span className="text-sm text-stone-400">Pin quote #{position}</span>
      </button>
    );
  }

  return (
    <div className="relative group rounded-2xl border border-stone-200 bg-stone-50 p-5 min-h-[120px] flex flex-col justify-between">
      <div className="flex items-start justify-between gap-2 mb-3">
        <Pin className="h-4 w-4 text-amber-500 shrink-0 mt-2.5" />
        <div className="flex gap-1.5">
          <button
            onClick={onPin}
            title="Change pin"
            aria-label="Change pinned quote"
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-500 transition hover:border-stone-300 hover:text-stone-700 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
          >
            <Pin className="h-4 w-4" />
          </button>
          <button
            onClick={onUnpin}
            title="Remove pin"
            aria-label="Remove pinned quote"
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-500 transition hover:border-rose-200 hover:text-rose-600 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
          >
            <PinOff className="h-4 w-4" />
          </button>
        </div>
      </div>
      <blockquote className="text-sm text-stone-800 leading-relaxed line-clamp-4 italic flex-1">
        "{pinnedQuote.text}"
      </blockquote>
      <p className="mt-3 text-xs font-medium text-stone-500">— {pinnedQuote.author}</p>
    </div>
  );
}

type ReferralCodeData = {
  code: string;
  referralUrl: string;
  referralCount: number;
};

const getGetReferralCodeQueryKey = () => ["/api/profile/referral-code"] as const;

function useGetReferralCode(options?: {
  query?: { queryKey?: readonly unknown[]; enabled?: boolean };
}) {
  return useQuery({
    queryKey: options?.query?.queryKey ?? getGetReferralCodeQueryKey(),
    queryFn: () =>
      customFetch<ReferralCodeData>("/api/profile/referral-code", { method: "GET" }),
    enabled: options?.query?.enabled,
    staleTime: Infinity,
  });
}

function ReferralCard() {
  const authed = useAuthEnabled();
  const [copied, setCopied] = useState(false);
  const referralQuery = useGetReferralCode({
    query: { queryKey: getGetReferralCodeQueryKey(), enabled: authed },
  });
  const data = referralQuery.data;

  async function handleCopy() {
    if (!data?.referralUrl) return;
    await navigator.clipboard.writeText(data.referralUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm xl:col-span-2">
      <h3 className="mb-1 text-xl font-semibold text-stone-900">Invite friends</h3>
      <p className="mb-5 text-sm text-stone-500">
        Share your personal link — anyone who signs up through it is counted as your referral.
      </p>

      {referralQuery.isLoading ? (
        <div className="space-y-3">
          <div className="h-11 animate-pulse rounded-xl bg-stone-100" />
          <div className="h-4 w-36 animate-pulse rounded bg-stone-100" />
        </div>
      ) : data ? (
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1 min-w-0 rounded-xl border border-stone-200 bg-stone-50 px-4 py-2.5 font-mono text-sm text-stone-700 truncate">
              {data.referralUrl}
            </div>
            <button
              onClick={handleCopy}
              className="shrink-0 rounded-xl bg-stone-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-stone-700 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <p className="text-sm text-stone-500">
            <span className="font-semibold text-stone-900">{data.referralCount.toLocaleString()}</span>{" "}
            {data.referralCount === 1 ? "person has" : "people have"} joined via your link
          </p>
        </div>
      ) : null}
    </div>
  );
}

function ProfileStatsBar({
  stats,
  isLoading,
}: {
  stats: ProfileStats | undefined;
  isLoading: boolean;
}) {
  const tiles = [
    {
      icon: <Bookmark className="h-5 w-5 text-amber-500" />,
      label: "Quotes saved",
      value: stats?.totalFavorites ?? 0,
    },
    {
      icon: <CheckCircle className="h-5 w-5 text-emerald-500" />,
      label: "Approved submissions",
      value: stats?.submissionCounts.approved ?? 0,
    },
    {
      icon: <ThumbsUp className="h-5 w-5 text-sky-500" />,
      label: "Upvotes earned",
      value: stats?.totalUpvotesReceived ?? 0,
    },
  ];

  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm xl:col-span-2">
      <h3 className="mb-4 text-xl font-semibold text-stone-900">Your stats</h3>
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className="flex flex-col items-center justify-center rounded-2xl bg-stone-50 p-3 sm:p-4 text-center gap-1.5 sm:gap-2"
          >
            {tile.icon}
            {isLoading ? (
              <div className="h-7 w-10 rounded bg-stone-200 animate-pulse mx-auto" />
            ) : (
              <p className="text-2xl sm:text-3xl font-bold text-stone-900">{tile.value.toLocaleString()}</p>
            )}
            <p className="text-[11px] sm:text-xs text-stone-500 leading-snug">{tile.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityHeatmap({
  activityData,
  isLoading,
}: {
  activityData: { date: string; count: number }[];
  isLoading: boolean;
}) {
  const countByDate = useMemo(() => {
    const map = new Map<string, number>();
    for (const { date, count } of activityData) {
      map.set(date, count);
    }
    return map;
  }, [activityData]);

  const { weeks, monthLabels } = useMemo(() => {
    const todayUTC = new Date();
    const todayStr = todayUTC.toISOString().slice(0, 10);
    const todayDay = todayUTC.getUTCDay();

    const firstSunday = new Date(Date.UTC(
      todayUTC.getUTCFullYear(),
      todayUTC.getUTCMonth(),
      todayUTC.getUTCDate() - todayDay - 51 * 7,
    ));

    const weeksArr: (string | null)[][] = [];
    const labelsArr: { col: number; label: string }[] = [];
    let lastMonth = -1;

    for (let col = 0; col < 52; col++) {
      const week: (string | null)[] = [];
      for (let day = 0; day < 7; day++) {
        const d = new Date(firstSunday);
        d.setUTCDate(d.getUTCDate() + col * 7 + day);
        const dStr = d.toISOString().slice(0, 10);
        week.push(dStr <= todayStr ? dStr : null);
      }
      const firstDay = week.find((d) => d !== null);
      if (firstDay) {
        const m = new Date(firstDay + "T00:00:00Z").getUTCMonth();
        if (m !== lastMonth) {
          labelsArr.push({
            col,
            label: new Date(firstDay + "T00:00:00Z").toLocaleDateString(undefined, {
              month: "short",
              timeZone: "UTC",
            }),
          });
          lastMonth = m;
        }
      }
      weeksArr.push(week);
    }

    return { weeks: weeksArr, monthLabels: labelsArr };
  }, []);

  const cellColor = (dateStr: string | null) => {
    if (!dateStr) return "bg-transparent";
    const count = countByDate.get(dateStr) ?? 0;
    if (count === 0) return "bg-stone-100";
    if (count === 1) return "bg-amber-200";
    if (count <= 3) return "bg-amber-400";
    return "bg-amber-600";
  };

  const cellTitle = (dateStr: string | null) => {
    if (!dateStr) return "";
    const count = countByDate.get(dateStr) ?? 0;
    if (count === 0) return dateStr;
    return `${dateStr} — ${count} quote${count === 1 ? "" : "s"} saved`;
  };

  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm xl:col-span-2 overflow-hidden">
      <h3 className="mb-1 text-xl font-semibold text-stone-900">Activity</h3>
      <p className="mb-4 text-sm text-stone-500">Days you saved a quote in the past year</p>

      {isLoading ? (
        <div className="h-24 rounded-2xl bg-stone-50 animate-pulse" />
      ) : (
        <div className="overflow-x-auto -mx-6 px-6 pb-1">
          <div className="w-max">
            <div className="flex gap-[3px] mb-1 ml-[22px]">
              {weeks.map((_, colIdx) => {
                const label = monthLabels.find((m) => m.col === colIdx);
                return (
                  <div key={colIdx} className="w-[14px] shrink-0 text-[10px] text-stone-400 leading-none">
                    {label?.label ?? ""}
                  </div>
                );
              })}
            </div>

            <div className="flex gap-[3px]">
              <div className="flex flex-col gap-[3px] mr-1">
                {["", "M", "", "W", "", "F", ""].map((label, i) => (
                  <div key={i} className="h-[14px] w-[14px] text-[10px] text-stone-400 leading-[14px] text-right pr-0.5">
                    {label}
                  </div>
                ))}
              </div>

              {weeks.map((week, colIdx) => (
                <div key={colIdx} className="flex flex-col gap-[3px]">
                  {week.map((dateStr, rowIdx) => (
                    <div
                      key={rowIdx}
                      title={cellTitle(dateStr)}
                      className={`h-[14px] w-[14px] rounded-[3px] ${cellColor(dateStr)}`}
                    />
                  ))}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-1.5 mt-3 text-[11px] text-stone-400">
              <span>Less</span>
              <div className="h-[12px] w-[12px] rounded-[2px] bg-stone-100" />
              <div className="h-[12px] w-[12px] rounded-[2px] bg-amber-200" />
              <div className="h-[12px] w-[12px] rounded-[2px] bg-amber-400" />
              <div className="h-[12px] w-[12px] rounded-[2px] bg-amber-600" />
              <span>More</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ProfilePage() {
  const queryClient = useQueryClient();
  const authed = useAuthEnabled();
  const clustersQuery = useGetProfileQuoteClusters({
    query: { queryKey: getGetProfileQuoteClustersQueryKey(), enabled: authed },
  });
  const favoritesQuery = useGetQuotesFavorites({
    query: { queryKey: getGetQuotesFavoritesQueryKey(), enabled: authed },
  });
  const pinnedQuery = useGetProfilePinnedQuotes({
    query: { queryKey: getGetProfilePinnedQuotesQueryKey(), enabled: authed },
  });
  const updatePins = useUpdateProfilePinnedQuotes();
  const statsQuery = useGetProfileStats({
    query: { queryKey: getGetProfileStatsQueryKey(), enabled: authed },
  });

  const { profile, isLoading: isProfileLoading } = useUserProfile();

  const [pickerSlot, setPickerSlot] = useState<number | null>(null);

  // Signed-out short-circuit (defense in depth — AppLayout normally
  // redirects, but render a friendly nudge if we ever do reach here).
  if (!authed) {
    return <AuthErrorPrompt />;
  }

  if (
    isAuthError(clustersQuery.error) ||
    isAuthError(favoritesQuery.error) ||
    isAuthError(pinnedQuery.error)
  ) {
    return <AuthErrorPrompt />;
  }

  const clusters = clustersQuery.data ?? [];
  const favoriteCount = favoritesQuery.data?.length ?? 0;
  const avatarSrc = profile?.avatarUrl ? `/api/storage${profile.avatarUrl}` : null;
  const displayName = profile?.username ?? null;

  const pinsByPosition = new Map(
    (pinnedQuery.data ?? []).map((p) => [p.position, p.quote]),
  );

  const currentPinnedQuoteIds = new Set(
    (pinnedQuery.data ?? []).map((p) => p.quote.id),
  );

  const handlePin = (slot: number, quote: QuoteWithTags) => {
    const existingPins: PinQuoteBody[] = (pinnedQuery.data ?? [])
      .filter((p) => p.position !== slot)
      .map((p) => ({ position: p.position, quoteId: p.quote.id }));

    const newPins: PinQuoteBody[] = [
      ...existingPins,
      { position: slot, quoteId: quote.id },
    ];

    updatePins.mutate(
      { data: newPins },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetProfilePinnedQuotesQueryKey() });
          setPickerSlot(null);
        },
      },
    );
  };

  const handleUnpin = (slot: number) => {
    const newPins: PinQuoteBody[] = (pinnedQuery.data ?? [])
      .filter((p) => p.position !== slot)
      .map((p) => ({ position: p.position, quoteId: p.quote.id }));

    updatePins.mutate(
      { data: newPins },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetProfilePinnedQuotesQueryKey() });
        },
      },
    );
  };

  return (
    <>
      {pickerSlot !== null && (
        <PinPickerModal
          favorites={favoritesQuery.data ?? []}
          currentPinnedQuoteIds={currentPinnedQuoteIds}
          onPick={(quote) => handlePin(pickerSlot, quote)}
          onClose={() => setPickerSlot(null)}
        />
      )}
      <section className="grid grid-cols-1 gap-6 xl:grid-cols-2" data-testid="page-profile">
        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm xl:col-span-2">
          <div className="flex items-center gap-4">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={displayName ?? "Avatar"}
                className="h-14 w-14 rounded-full object-cover shrink-0"
              />
            ) : (
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-stone-200 text-xl font-semibold text-stone-600">
                {displayName?.[0]?.toUpperCase() ?? "?"}
              </div>
            )}
            <div>
              <h2 className="text-xl font-semibold text-stone-900">{displayName ?? "Your profile"}</h2>
              <p className="text-sm text-stone-500">
                {favoriteCount > 0
                  ? `${favoriteCount} saved quote${favoriteCount === 1 ? "" : "s"}`
                  : "No saved quotes yet"}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm xl:col-span-2">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-stone-900">Pinned quotes</h3>
              <p className="text-sm text-stone-500 mt-0.5">Showcase up to 3 quotes that represent you (These carry extra weight in calculating your quote profile)</p>
            </div>
            {favoriteCount === 0 && !pinnedQuery.isLoading && (
              <p className="text-xs text-stone-400">Save some quotes first</p>
            )}
          </div>
          {pinnedQuery.isLoading ? (
            <p className="text-sm text-stone-400 animate-pulse">Loading…</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-3">
              {[1, 2, 3].map((slot) => (
                <PinnedQuoteSlot
                  key={slot}
                  position={slot}
                  pinnedQuote={pinsByPosition.get(slot) ?? null}
                  onPin={() => setPickerSlot(slot)}
                  onUnpin={() => handleUnpin(slot)}
                />
              ))}
            </div>
          )}
        </div>

        <ReferralCard />

        <ProfileStatsBar
          stats={statsQuery.data}
          isLoading={statsQuery.isLoading}
        />

        <ActivityHeatmap
          activityData={statsQuery.data?.activityData ?? []}
          isLoading={statsQuery.isLoading}
        />

        <NotificationsCard
          initialEnabled={profile?.notifyEnabled ?? false}
          initialFrequency={(profile?.notifyFrequency as "daily" | "weekly") ?? "daily"}
          initialHour={profile?.notifyHour ?? 8}
          initialEmail={profile?.notifyEmail ?? null}
        />

        <ContentPreferencesCard
          initialAllowProfane={profile?.allowProfaneQuotes ?? false}
          initialAllowSexual={profile?.allowSexualQuotes ?? false}
          isLoading={isProfileLoading}
        />

        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-1">
            <Palette className="h-5 w-5 text-stone-400" />
            <h3 className="text-xl font-semibold text-stone-900">Appearance</h3>
          </div>
          <p className="mb-6 text-sm text-stone-500">
            Choose a mood that shapes how the app looks and feels.
          </p>
          <ThemePicker />
        </div>

        <SubmitQuoteCard renderHistory={(submissions) => <SubmissionHistoryList submissions={submissions} />} />

        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
          <div className="mb-2 flex items-center gap-1.5">
            <h3 className="text-xl font-semibold text-stone-900">Quote profile snapshot</h3>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="How these percentages are calculated"
                  className="text-stone-400 hover:text-stone-600 transition-colors"
                >
                  <Info className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-64 text-left">
                These percentages are relative, not absolute — your strongest
                theme is always shown as 100%, and the rest are scaled against
                it. They show how your favorite themes compare to each other,
                not a confidence score.
              </TooltipContent>
            </Tooltip>
          </div>
          <p className="mb-6 text-sm leading-relaxed text-stone-500">
            {favoriteCount > 0
              ? `Based on ${favoriteCount} saved quote${favoriteCount === 1 ? "" : "s"}, your profile leans toward these themes.`
              : "Save some quotes to see your personalised thematic profile."}
          </p>
          {clusters.length > 0 ? (
            <div className="space-y-5">
              {clusters.map((c) => (
                <MetricBar key={c.label} label={c.label} value={c.percentage} />
              ))}
            </div>
          ) : clustersQuery.isLoading ? (
            <p className="text-sm text-stone-400 animate-pulse">Loading profile…</p>
          ) : (
            <div className="space-y-5">
              <MetricBar label="Resilience" value={0} />
              <MetricBar label="Wisdom" value={0} />
              <MetricBar label="Ambition" value={0} />
              <MetricBar label="Poetic reflection" value={0} />
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
          <h3 className="mb-5 text-xl font-semibold text-stone-900">Top affinities</h3>
          {favoriteCount > 0 && favoritesQuery.data ? (
            <div className="space-y-4">
              {Array.from(
                new Map(
                  (favoritesQuery.data ?? []).map((q) => [q.author, q]),
                ).values(),
              )
                .slice(0, 4)
                .map((q, index) => (
                  <div key={q.author} className="flex items-center justify-between rounded-2xl bg-stone-50 p-4">
                    <div>
                      <p className="font-medium text-stone-900">{q.author}</p>
                      <p className="text-sm text-stone-500">Affinity rank #{index + 1}</p>
                    </div>
                    <span className="text-stone-400 font-medium">✓</span>
                  </div>
                ))}
            </div>
          ) : (
            <div className="space-y-4">
              {["Marcus Aurelius", "Emily Dickinson", "Laozi", "Friedrich Nietzsche"].map((author, index) => (
                <div key={author} className="flex items-center justify-between rounded-2xl bg-stone-50 p-4">
                  <div>
                    <p className="font-medium text-stone-900">{author}</p>
                    <p className="text-sm text-stone-500">Affinity rank #{index + 1}</p>
                  </div>
                  <span className="text-stone-400 font-medium">✓</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
